import React, { useState, useEffect, useMemo } from 'react';
import { askSessionPath, libraryDocumentPath } from '@/constants/appRoutes';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { FolderOpen, FilterX, Plus } from 'lucide-react';
import { projectsApi, documentApi, Project, Document, DocumentListItem, ProjectChatItem, ProjectFileItem } from '../../services/api';
import ProjectsLayout from './components/ProjectsLayout';
import { useNavbarToolbar } from '../../contexts/NavbarToolbarContext';
import { useBreadcrumbLabel } from '../../contexts/BreadcrumbContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Spinner } from '@/components/ui/spinner';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useProjectsData } from './hooks/useProjectsData';
import { useProjectResources } from './hooks/useProjectResources';
import { DocumentDetailsDialog } from './components/DocumentDetailsDialog';
import { ProjectsSearchToolbar } from './components/ProjectsSearchToolbar';
import { ProjectsFilterPopover } from './components/ProjectsFilterPopover';
import { ProjectResourcesList, ProjectListItem } from './components/ProjectResourcesList';
import { useToast } from '@/hooks/use-toast';

const ProjectsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const docIdFromUrl = searchParams.get('doc');
  const { setToolbar } = useNavbarToolbar();
  const { toast } = useToast();
  const { setBreadcrumbLabel, setBreadcrumbSuffix } = useBreadcrumbLabel();
  const { projects, sharedProjects, loading, refresh } = useProjectsData();
  const {
    documents: savedDocuments,
    chats: savedChats,
    files: savedFiles,
    loading: docsLoading,
    refresh: refreshResources,
  } = useProjectResources();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('');
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [createOpen, setCreateOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sidebarDocumentId, setSidebarDocumentId] = useState<string | null>(null);
  const [sidebarDocument, setSidebarDocument] = useState<Document | null>(null);
  const [sidebarLoading, setSidebarLoading] = useState(false);

  useEffect(() => {
    setBreadcrumbLabel('All resources');
    return () => setBreadcrumbLabel(null);
  }, [setBreadcrumbLabel]);

  useEffect(() => {
    if (docIdFromUrl) {
      setSidebarDocumentId(docIdFromUrl);
    } else {
      setSidebarDocumentId(null);
    }
  }, [docIdFromUrl]);

  useEffect(() => {
    if (!sidebarDocumentId) {
      setSidebarDocument(null);
      setSidebarLoading(false);
      return;
    }
    let cancelled = false;
    setSidebarLoading(true);
    documentApi
      .getDocument(sidebarDocumentId)
      .then((doc) => {
        if (!cancelled) setSidebarDocument(doc);
      })
      .catch(() => {
        if (!cancelled) setSidebarDocument(null);
      })
      .finally(() => {
        if (!cancelled) setSidebarLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sidebarDocumentId]);

  useEffect(() => {
    if (sidebarDocument?.title) {
      setBreadcrumbSuffix(sidebarDocument.title);
    } else {
      setBreadcrumbSuffix(null);
    }
    return () => setBreadcrumbSuffix(null);
  }, [sidebarDocument?.title, setBreadcrumbSuffix]);

  const openDocumentInSidebar = (docId: string) => {
    setSidebarDocumentId(docId);
    navigate(`/projects?doc=${docId}`, { replace: true });
  };

  const closeDocumentSidebar = () => {
    setSidebarDocumentId(null);
    setSidebarDocument(null);
    navigate('/projects', { replace: true });
  };

  type ResourceKind = 'Document' | 'Chat' | 'PDF';

  interface ResourceItem {
    id: string;
    title: string;
    kind: ResourceKind;
    typeLabel: string;
    created_at: string;
    updated_at?: string | null;
    category?: string;
  }

  const allResources = useMemo<ResourceItem[]>(() => {
    const docs: ResourceItem[] = savedDocuments.map((d) => ({
      id: d.id,
      title: d.title,
      kind: 'Document',
      typeLabel: d.tags?.[0]?.label || 'Document',
      created_at: d.created_at,
      updated_at: d.updated_at,
      category: d.tags?.[0]?.slug ?? d.tags?.[0]?.root_code ?? undefined,
    }));

    const chats: ResourceItem[] = savedChats.map((c) => ({
      id: c.id,
      title: c.title?.trim() || 'New chat',
      kind: 'Chat',
      typeLabel: 'Chat',
      created_at: c.created_at,
      updated_at: c.updated_at,
    }));

    const files: ResourceItem[] = savedFiles.map((f) => ({
      id: f.id,
      title: f.file_name,
      kind: 'PDF',
      typeLabel: 'PDF',
      created_at: f.created_at,
      updated_at: undefined,
    }));

    return [...docs, ...chats, ...files].sort((a, b) => {
      const ta = (a.updated_at || a.created_at)
        ? new Date(a.updated_at || a.created_at).getTime()
        : 0;
      const tb = (b.updated_at || b.created_at)
        ? new Date(b.updated_at || b.created_at).getTime()
        : 0;
      return tb - ta;
    });
  }, [savedDocuments, savedChats, savedFiles]);

  const resources = useMemo(() => {
    let list = allResources;
    if (filterType) {
      list = list.filter(
        (r) => r.kind === 'Document' && r.typeLabel === filterType
      );
    }
    if (filterCategory) {
      list = list.filter(
        (r) =>
          r.kind === 'Document' && (r.category || '') === filterCategory
      );
    }
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter((r) => {
        const title = (r.title || '').toLowerCase();
        const typeStr = (r.typeLabel || '').toLowerCase();
        const cat = (r.category || '').toLowerCase();
        return title.includes(q) || typeStr.includes(q) || cat.includes(q);
      });
    }
    return list;
  }, [allResources, filterType, filterCategory, searchQuery]);

  useEffect(() => {
    if ((location.state as { create?: boolean })?.create) {
      setCreateOpen(true);
      navigate('/projects', { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  const documentTypes = useMemo(() => {
    const set = new Set(savedDocuments.map((d) => d.tags?.[0]?.label ?? '').filter(Boolean));
    return Array.from(set).sort();
  }, [savedDocuments]);

  const categories = useMemo(() => {
    const set = new Set(
      savedDocuments
        .map((d) => d.tags?.[0]?.slug ?? d.tags?.[0]?.root_code ?? '')
        .filter(Boolean),
    );
    return Array.from(set).sort();
  }, [savedDocuments]);

  const handleCreateOpen = () => {
    setName('');
    setDescription('');
    setCreateOpen(true);
  };

  const handleCreateClose = () => {
    setCreateOpen(false);
  };

  const handleCreate = async () => {
    if (!name.trim()) return;
    try {
      await projectsApi.create({ name: name.trim(), description: description.trim() || undefined });
      handleCreateClose();
      await refresh();
    } catch (error) {
      console.error('Error creating project:', error);
    }
  };

  const handleEditOpen = (project: Project) => {
    setEditingProject(project);
    setName(project.name);
    setDescription(project.description || '');
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditOpen(false);
    setEditingProject(null);
  };

  const handleEdit = async () => {
    if (!editingProject || !name.trim()) return;
    try {
      await projectsApi.update(editingProject.id, {
        name: name.trim(),
        description: description.trim() || undefined,
      });
      handleEditClose();
      await refresh();
    } catch (error) {
      console.error('Error updating project:', error);
    }
  };

  const handleDelete = async (project: Project) => {
    if (!window.confirm(`Delete library "${project.name}"? Documents will not be deleted.`)) return;
    try {
      await projectsApi.delete(project.id);
      await refresh();
      navigate('/projects');
    } catch (error) {
      console.error('Error deleting project:', error);
    }
  };

  useEffect(() => {
    const toolbar = (
      <ProjectsSearchToolbar
        placeholder="Search all resources…"
        ariaLabel="Search resources"
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
      />
    );
    setToolbar(toolbar);
    return () => setToolbar(null);
  }, [searchQuery, setToolbar]);

  const documentDialogOpen = !!sidebarDocumentId;

  const listItems: ProjectListItem[] = useMemo(
    () =>
      resources.map((item) => ({
        id: item.id,
        title: item.title,
        kind: item.kind,
        typeLabel: item.kind === 'Document' ? item.typeLabel : item.typeLabel,
        category: item.category,
        createdAt: item.created_at,
        updatedAt: item.updated_at,
      })),
    [resources]
  );

  return (
    <>
      <DocumentDetailsDialog
        open={documentDialogOpen}
        loading={sidebarLoading}
        document={sidebarDocument}
        onOpenChange={(open) => {
          if (!open) closeDocumentSidebar();
        }}
        onOpenFull={() => {
          if (sidebarDocument) {
            navigate(libraryDocumentPath(sidebarDocument.id));
          }
        }}
        onOpenSource={
          sidebarDocument?.source_url
            ? () => window.open(sidebarDocument.source_url as string, '_blank')
            : undefined
        }
      />

      <ProjectsLayout
        projects={projects}
        loading={loading}
        onRefresh={refreshResources}
        onCreateOpen={handleCreateOpen}
        onEdit={handleEditOpen}
        onDelete={handleDelete}
        sharedProjects={sharedProjects}
        onItemDrop={async (targetProjectId, payload) => {
          try {
            for (const item of payload) {
              if (item.kind === 'Document') {
                await projectsApi.addDocument(targetProjectId, item.id);
              } else if (item.kind === 'Chat') {
                await projectsApi.addChat(targetProjectId, item.id);
              }
            }
            await refreshResources();
            toast({
              title: 'Added to library',
              description: 'Item was added to the selected library.',
            });
          } catch (error) {
            console.error('Error adding item to project via drag-and-drop:', error);
            toast({
              title: 'Error',
              description: 'Could not add item to library.',
              variant: 'destructive',
            });
          }
        }}
      >
        <div className="w-full flex flex-col h-full">
          {docsLoading ? (
            <div className="flex-1 flex justify-center items-center py-12">
              <Spinner className="h-8 w-8" />
            </div>
          ) : resources.length === 0 ? (
            <div className="flex-1 flex items-center justify-center py-16">
              <div className="rounded-xl border border-dashed border-border bg-muted/30 p-12 text-center max-w-md mx-auto">
                {allResources.length === 0 ? (
                  <>
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                      <FolderOpen className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      No resources yet
                    </h3>
                    <p className="text-sm text-muted-foreground mb-6">
                      Start saving documents to your libraries. Open any document and use &quot;Save to project&quot; to add it here.
                    </p>
                    <Button
                      onClick={handleCreateOpen}
                      variant="outline"
                      size="sm"
                    >
                      Create your first library
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
                      <FilterX className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      No matching resources
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Try adjusting your filters to see more results.
                    </p>
                  </>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-4 pl-4 pr-6 py-3 border-b border-border flex-shrink-0 bg-muted/30">
                <h1 className="text-xl font-bold text-foreground tracking-tight truncate min-w-0 flex-1">
                  All resources
                </h1>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <ProjectsFilterPopover
                    mode="resources"
                    ariaLabel="Filter by type and category"
                    documentTypes={documentTypes}
                    categories={categories}
                    filterType={filterType}
                    onFilterTypeChange={setFilterType}
                    filterCategory={filterCategory}
                    onFilterCategoryChange={setFilterCategory}
                  />
                  {/* 
                  <Button
                    onClick={() => setAddDialogOpen(true)}
                    size="sm"
                    className="gap-2 h-9"
                  >
                    <Plus className="h-4 w-4" />
                    Add
                  </Button>
                  */}
                </div>
              </div>
              <ProjectResourcesList
                items={listItems}
                onRowClick={(item) => {
                  if (item.kind === 'Document') openDocumentInSidebar(item.id);
                  else if (item.kind === 'Chat') navigate(askSessionPath(item.id));
                }}
                selectable={false}
              />
            </>
          )}
        </div>
      </ProjectsLayout>

      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">TODO</p>
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New library</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My Projects"
                className="mt-1"
                autoFocus
              />
            </div>
            <div>
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="A brief description..."
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCreateClose}>Cancel</Button>
            <Button onClick={handleCreate} disabled={!name.trim()}>
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit library</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My Projects"
                className="mt-1"
                autoFocus
              />
            </div>
            <div>
              <Label htmlFor="edit-description">Description (optional)</Label>
              <Textarea
                id="edit-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="A brief description..."
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleEditClose}>Cancel</Button>
            <Button onClick={handleEdit} disabled={!name.trim()}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProjectsPage;
